<template>
    <v-card class="activity-grouped-offers" elevation="0">
        <div v-if="fullCard" class="mb-5">
            <v-card-title class="activity-grouped-offers__title text-h6 font-weight-light align-baseline">
                {{ $t('available_offers') }}
                <span
                    v-if="!isAlwaysVisibleFilters"
                    class="ml-4 text-body-2 text-primary cursor-pointer"
                    @click="expanded = !expanded"
                >
                    {{ $t('filters') }}
                    <v-icon color="primary" size="small">
                        {{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                    </v-icon>
                </span>
            </v-card-title>
            <v-expand-transition v-model="expanded">
                <v-row v-if="expanded || isAlwaysVisibleFilters" class="activity-grouped-offers__filters" no-gutters>
                    <v-col cols="12" md="4" class="mr-0 mr-md-3">
                        <sort-select v-model="sortKey" :items="sortItems" />
                    </v-col>
                    <v-col cols="12" md="3" class="mr-0 mr-md-3">
                        <group-offers-select
                            class="pt-3"
                            :items="['none', 'serviceName', 'date']"
                            :product-store="productStore"
                        />
                    </v-col>
                    <v-col cols="12" md="3" class="pt-md-3">
                        <date-filter-search
                            v-model="dateFilter"
                            :dates-for-filter="datesForFilter"
                            @update:model-value="onDateFilterChange"
                        />
                    </v-col>
                </v-row>
            </v-expand-transition>
        </div>
        <v-expansion-panels v-if="groupOffersBy !== 'none'" v-model="panels" multiple variant="accordion">
            <v-expansion-panel v-for="(offers, groupIndex) in groupedOffers" :key="`group-${groupIndex}`">
                <v-expansion-panel-title
                    class="d-flex align-baseline text-nobreak font-weight-bold"
                    :class="$breakpoint.smAndDown ? 'text-body-2' : 'text-subtitle-1'"
                    color="blue-grey-lighten-5"
                >
                    <div class="flex-shrink-1">
                        <div v-if="groupOffersBy === 'date'">
                            {{ $filters.dateFormat(offers[0][groupOffersBy]) }}
                        </div>
                        <div v-else>
                            {{ offers[0][groupOffersBy] }}
                        </div>
                    </div>
                    <div class="pl-2 pr-4 flex-shrink-0 font-weight-bold text-right">
                        <v-chip
                            label
                            size="x-small"
                            :color="minPriceOffer(offers).available ? 'green-darken-1' : 'orange-lighten-3'"
                            class="activity-grouped-offers__chip-available mr-4 text-white"
                            :class="{available: minPriceOffer(offers).available}"
                        >
                            {{ minPriceOffer(offers).available ? $t('available') : $t('on_request') }}
                        </v-chip>
                        <template
                            v-if="
                                minPriceOffer(offers).price &&
                                minPriceOffer(offers).price.amount &&
                                !hidePrice &&
                                showPriceDuringSearch
                            "
                        >
                            {{ $t('from') }} {{ $filters.price(minPriceOffer(offers).price) }}
                        </template>
                    </div>
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                    <activity-offer-card
                        v-for="(offer, offerIndex) in offers"
                        :key="offerIndex"
                        :offer="offer"
                        :full-card="fullCard"
                        :grouped-by="groupOffersBy"
                        :data-cy="`activity-offer-card-${offerIndex}`"
                        @images="$emit('images', $event)"
                        @info="$emit('info', $event)"
                    >
                        <template v-slot:price>
                            <slot name="price" :offer="offer" :offer-key="offer.offerKey" />
                        </template>
                    </activity-offer-card>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </v-card>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Setup, Vue, Watch} from 'vue-facing-decorator'
    import ActivityOfferCard from '~src/components/activities/info/activityOfferCard.src'
    import SortSelect from '@/components/search/SortSelect.vue'
    import GroupOffersSelect from '@/components/search/GroupOffersSelect.vue'
    import DateFilterSearch from '@/components/snippets/DateFilterSearch.vue'

    @Component({
        components: {
            DateFilterSearch,
            ActivityOfferCard,
            SortSelect,
            GroupOffersSelect,
        },
    })
    export class ActivityGroupedOffers extends Vue {
        @Prop({default: () => []}) sortedFilteredOffers
        @Prop({default: false, type: Boolean}) fullCard
        @Prop({default: false, type: Boolean}) hidePrice
        @Prop() datesForFilter
        @Setup(() => useBookingPriceButton().showPriceDuringSearch) showPriceDuringSearch

        dateFilter = null
        panels = [0]
        expanded = false

        @Watch('groupOffersBy')
        onGroupOffersByChange() {
            this.panels = [0]
        }

        minPriceOffer(offers) {
            return minPriceOffer({offers})
        }

        @Emit()
        onDateFilterChange(date) {
            return date
        }

        get groupedOffers() {
            let offers = [...this.sortedFilteredOffers]
            return offers.reduce((groupedOffers, offer) => {
                let group = groupedOffers.find(group => group[0][this.groupOffersBy] === offer[this.groupOffersBy])
                if (!group) {
                    group = []
                    groupedOffers.push(group)
                }
                group.push(offer)
                return groupedOffers
            }, [])
        }

        get sortKey() {
            return activitiesStore.sortFnName
        }

        set sortKey(sortKey) {
            activitiesStore.SET_SORT(sortKey)
        }

        get productStore() {
            return activitiesStore
        }

        get groupOffersBy() {
            return this.productStore.groupOffersBy
        }

        get sortItems() {
            return [
                {
                    text: this.$t('sort.price_asc'),
                    value: 'priceAsc',
                },
                {
                    text: this.$t('sort.price_desc'),
                    value: 'priceDesc',
                },
                {
                    text: this.$t('sort.date_asc'),
                    value: 'dateAsc',
                },
            ]
        }

        get isAlwaysVisibleFilters() {
            return false
        }
    }

    export default ActivityGroupedOffers
</script>

<style scoped>
    :deep(.v-expansion-panel-content__wrap) {
        padding-left: 0;
        padding-right: 0;
    }
</style>
